/**
 * The Divisions route table (FSD 1.2.2).
 *
 * `web/src/router/index.ts` and `web/src/nav/parts/p01-enterprise.ts` are shared
 * files sibling C14 packets also touch and this packet does NOT own — the
 * orchestrator spreads `DIVISION_ROUTES` into the router and adds the nav entry
 * once every packet in this batch has landed.
 *
 * Every route is gated on the **read** permission: creating, editing, mapping to a
 * segment, associating countries/entities, defining cross-charging and reorganising
 * are all checked on the action itself (1.2.2.10: divisional owners hold read
 * access to their own division and its rollup, and no write access to the division
 * record itself), so an Auditor holding only `org.division.read` can open the
 * register and every division and change nothing.
 */

import type { RouteRecordRaw } from 'vue-router'

import { DIVISION_PERMISSIONS } from '@/modules/enterprise/divisions/permissions'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'

export const DIVISIONS_BASE = '/enterprise/divisions'

export function costCentresRoute(): string {
  return `${DIVISIONS_BASE}/cost-centres`
}

export function crossChargesRoute(): string {
  return `${DIVISIONS_BASE}/cross-charges`
}

export function rollupRoute(): string {
  return `${DIVISIONS_BASE}/rollup`
}

export function divisionsRoute(): string {
  return DIVISIONS_BASE
}

export function divisionRoute(id: number): string {
  return `${DIVISIONS_BASE}/${id}`
}

export const DIVISION_ROUTES: RouteRecordRaw[] = [
  {
    path: DIVISIONS_BASE,
    name: 'enterprise-divisions',
    component: () => import('@/modules/enterprise/divisions/DivisionsListView.vue'),
    meta: {
      titleKey: 'enterprise.divisions.routeTitles.list',
      permission: DIVISION_PERMISSIONS.read,
    },
  },
  // The three FSD 1.2.2 menu screens below sit before the `:id` route so a
  // literal segment can never be read as a division id.
  {
    path: `${DIVISIONS_BASE}/cost-centres`,
    name: 'enterprise-division-cost-centres',
    component: () => import('@/modules/enterprise/divisions/CostCentresView.vue'),
    meta: {
      titleKey: 'enterprise.divisions.routeTitles.costCentres',
      permission: ORG_PERMISSIONS.unitRead,
    },
  },
  {
    path: `${DIVISIONS_BASE}/cross-charges`,
    name: 'enterprise-division-cross-charges',
    component: () => import('@/modules/enterprise/divisions/CrossChargesView.vue'),
    meta: {
      titleKey: 'enterprise.divisions.routeTitles.crossCharges',
      permission: DIVISION_PERMISSIONS.crossChargeRead,
    },
  },
  {
    path: `${DIVISIONS_BASE}/rollup`,
    name: 'enterprise-division-rollup',
    component: () => import('@/modules/enterprise/divisions/RollupView.vue'),
    meta: {
      titleKey: 'enterprise.divisions.routeTitles.rollup',
      permission: DIVISION_PERMISSIONS.read,
    },
  },
  {
    path: `${DIVISIONS_BASE}/:id(\\d+)`,
    name: 'enterprise-division',
    component: () => import('@/modules/enterprise/divisions/DivisionView.vue'),
    meta: {
      titleKey: 'enterprise.divisions.routeTitles.detail',
      permission: DIVISION_PERMISSIONS.read,
    },
  },
]
